import { useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useIoContext, useOverlayContext } from '~/contexts';
import { QueryKeys } from '~/hooks/reactQuery/queryKeys';
import { useMutationState } from '~/hooks/reactQuery/useMutationState';
import { useLocaleKeys } from '~/i18n';
import { makeFailure, makeSuccess, sendErrReport } from '~/infra/utils';
import { legalTermsModel } from '~/models';
import { useAppConfig, useStoreActions } from '~/store/store-context';
import { NameThisDocumentModalError } from '../../../NameThisDocumentModal/NameThisDocumentModal';
export const useUploadLegalTerms = (param)=>{
    let { onSettled, errorToastTestId } = param;
    const { t: legalTexts } = useLocaleKeys('components.legalTermsDropdown');
    const { api } = useStoreActions();
    const queryClient = useQueryClient();
    const overlayContext = useOverlayContext();
    const { generateUniqueId } = useAppConfig();
    const { browser: { uploadFileToCloudStorage } } = useIoContext();
    const { mutateAsync: uploadLegalTerms, isLoading } = useMutationState({
        mutationFn: async (param)=>{
            let { file, newFileName } = param;
            try {
                const { fileId, url } = await api.business.getLegalTermsUploadURL();
                await uploadFileToCloudStorage(url, file, {
                    'content-type': 'application/pdf'
                });
                const uploadedLegalTerms = legalTermsModel.makePdfLegalTerms({
                    name: newFileName,
                    pdfUniqueName: fileId,
                    pdfFileName: file.name
                }, generateUniqueId);
                await api.business.createLegalTerms(uploadedLegalTerms);
                return makeSuccess(uploadedLegalTerms);
            } catch (error) {
                var _error_response;
                if (error instanceof AxiosError && ((_error_response = error.response) === null || _error_response === void 0 ? void 0 : _error_response.status) === 400 && NameThisDocumentModalError[error.response.data.error]) {
                    return makeFailure(error.response.data.error);
                }
                return makeFailure(error);
            }
        },
        onSettled: async (result)=>{
            if (result === null || result === void 0 ? void 0 : result.success) {
                await queryClient.invalidateQueries({
                    queryKey: [
                        QueryKeys.LegalTerms
                    ]
                });
                onSettled(result.data);
            } else {
                if (!NameThisDocumentModalError[result === null || result === void 0 ? void 0 : result.error]) {
                    const message = legalTexts.unknownError();
                    sendErrReport(result === null || result === void 0 ? void 0 : result.error);
                    overlayContext.showErrorToast(message, errorToastTestId);
                }
                onSettled(undefined);
            }
        }
    });
    return {
        uploadLegalTerms,
        isLoading
    };
};
